import { useState } from 'react'
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, GoogleAuthProvider, signInWithPopup } from 'firebase/auth'
import { auth } from '../config/firebase'
import ProfilesService from '../services/ProfilesService'
import { translateFirebaseError } from '../utils/firebaseErrors'

interface ClientLoginProps {
  isOpen: boolean
  onClose: () => void
  onLogin: (user: { id: string; username: string; email: string }, rememberMe: boolean) => void
  onAdminLogin?: (rememberMe: boolean) => void
  onSetRoleSwitching?: (value: boolean) => void
  isAdminLoggedIn?: boolean
  onAdminLogout?: () => void
}

const ClientLogin = ({ isOpen, onClose, onLogin, onAdminLogin, onSetRoleSwitching, isAdminLoggedIn, onAdminLogout }: ClientLoginProps) => {
  const [username, setUsername] = useState('')
  const [newUsername, setNewUsername] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [loading, setLoading] = useState(false)
  const [loadingGoogle, setLoadingGoogle] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  // Clear form fields when popup opens/closes
  const clearForm = () => {
    setUsername('')
    setNewUsername('')
    setCompanyName('')
    setPassword('')
    setConfirmPassword('')
    setError('')
    setRememberMe(false)
  }

  // Clear form when popup closes
  const handleClose = () => {
    clearForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('🚀 Form submitted! Username:', username, 'Mode:', mode)
    if (loading) return
    setLoading(true)
    
    try {
      if (mode === 'login') {
    // Login: sprawdźmy czy to email czy username
    let emailToLogin = username.trim()
    console.log('🔍 Original input:', emailToLogin)
    console.log('🔍 Lowercase check:', emailToLogin.toLowerCase())
    console.log('🔍 Is admin?', emailToLogin.toLowerCase() === 'admin')
    
    // Specjalne sprawdzenie dla admina - "admin" lub "Admin" = konfiguratorunico@gmail.com
    if (emailToLogin.toLowerCase() === 'admin') {
      emailToLogin = 'konfiguratorunico@gmail.com'
      console.log('🔑 Admin login shortcut detected, using admin email:', emailToLogin)
    }
    // Jeśli nie zawiera @, to prawdopodobnie to username - znajdź email w bazie
    else if (!emailToLogin.includes('@')) {
      console.log('🔍 Looking for user by username:', emailToLogin)
      const userProfile = await ProfilesService.findByUsernameOrEmail(emailToLogin)
      if (!userProfile) {
        throw new Error('Nie znaleziono użytkownika o podanej nazwie')
      }
      if (userProfile.deleted) {
        throw new Error('To konto zostało usunięte przez administratora')
      }
      emailToLogin = userProfile.email
      console.log('✅ Found user email for username:', emailToLogin)
    }
    
    // TYLKO dla logowania NIE-ADMINÓW wyłącz listenery
    if (emailToLogin !== 'konfiguratorunico@gmail.com' && onSetRoleSwitching) {
      console.log('🛑 Disabling all listeners for client login')
      onSetRoleSwitching(true)
    }
    
    // Sprawdź czy admin jest zalogowany i wyloguj go przed logowaniem usera (nie dla admina)
    if (emailToLogin !== 'konfiguratorunico@gmail.com' && isAdminLoggedIn && onAdminLogout) {
      console.log('🔄 Admin → User switch: logging out admin first')
      alert('Zalogowano się jako użytkownik. Zostaniesz wylogowany z konta Administratora.')
      onAdminLogout()
    }
    
    console.log('🔥 About to call Firebase with email:', emailToLogin, 'password length:', password.length)
    const cred = await signInWithEmailAndPassword(auth, emailToLogin, password)
    const u = cred.user
    console.log('🎉 Firebase login successful, user:', u.email)
        
        // Check if user profile exists and is not deleted
        const profile = await ProfilesService.getByUid(u.uid)
        console.log('👤 Profile loaded:', profile)
        if (profile?.deleted) {
          alert('To konto zostało usunięte przez administratora.')
          await auth.signOut()
          throw new Error('Konto usunięte')
        }
        
        // DODATKOWE ZABEZPIECZENIE: Sprawdź rolę użytkownika przed zalogowaniem
        console.log('🔐 Final role verification before login...')
        await new Promise(resolve => setTimeout(resolve, 500)) // Opóźnienie dla stabilności
        
        const finalProfile = await ProfilesService.getByUid(u.uid)
        if (!finalProfile || finalProfile.deleted) {
          await auth.signOut()
          throw new Error('Profil użytkownika nie został znaleziony lub konto zostało usunięte')
        }
        
        console.log('✅ Final verification passed, role:', finalProfile.role)
        
        // Obsługa logowania w zależności od roli
        if (finalProfile.role === 'admin') {
          // Użytkownik ma rolę admin
          console.log('🔧 Admin role detected, calling onAdminLogin')
          if (onAdminLogin) {
            onAdminLogin(rememberMe)
            console.log('✅ Admin login handler called successfully')
            
            // Dla admina - opóźnij zamknięcie modala
            setTimeout(() => {
              clearForm()
              onClose()
            }, 100)
          } else {
            console.error('❌ No onAdminLogin handler provided!')
            throw new Error('Brak obsługi logowania administratora')
          }
        } else if (finalProfile.role === 'client') {
          // Użytkownik ma rolę client
          console.log('👤 Client role detected, calling onLogin')
          onLogin({ id: u.uid, username: u.email?.split('@')[0] || 'user', email: u.email || '' }, rememberMe)
          
          // Dla klienta - natychmiastowe zamknięcie
          clearForm()
          onClose()
        } else {
          await auth.signOut()
          throw new Error('Nieznana rola użytkownika')
        }
        
        // Już nie wywołujemy clearForm() i onClose() tutaj - przeniesione do odpowiednich ról
        
        // Włącz z powrotem listenery po krótkiej chwili
        setTimeout(() => {
          if (onSetRoleSwitching) {
            console.log('✅ Re-enabling listeners after client login')
            onSetRoleSwitching(false)
          }
        }, 2000) // 2 sekundy żeby się wszystko ustabilizowało
      } else {
        // Register: username field is email; newUsername is optional display/handle
        const email = username.trim()
        const uname = (newUsername || email.split('@')[0]).trim()
        if (!email.includes('@')) throw new Error('Podaj poprawny e‑mail')
        if (password.length < 6) throw new Error('Hasło musi mieć co najmniej 6 znaków')
        if (password !== confirmPassword) throw new Error('Hasła nie są jednakowe')
        
        // Check if email belongs to a suspended account
        const emailStatus = await ProfilesService.checkEmailStatus(email)
        if (emailStatus.exists && emailStatus.deleted) {
          alert(`⚠️ KONTO ZAWIESZONE\n\nKonto z adresem ${email} zostało zawieszone przez administratora.\n\nW celu wyjaśnienia sytuacji proszę skontaktować się z administratorem strony.`)
          throw new Error('Konto zawieszone przez administratora')
        }
        
        // Check if username belongs to a suspended account  
        if (uname) {
          const usernameStatus = await ProfilesService.checkUsernameStatus(uname)
          if (usernameStatus.exists && usernameStatus.deleted) {
            alert(`⚠️ NAZWA UŻYTKOWNIKA ZAWIESZONA\n\nNazwa użytkownika "${uname}" należy do konta zawieszonego przez administratora.\n\nW celu wyjaśnienia sytuacji proszę skontaktować się z administratorem strony.`)
            throw new Error('Nazwa użytkownika należy do zawieszonego konta')
          }
        }
        
        // Opcjonalne sprawdzenie unikalności nazwy użytkownika
        if (uname) {
          const taken = await ProfilesService.isUsernameTaken(uname)
          if (taken) throw new Error('Ta nazwa użytkownika jest zajęta')
        }
        const cred = await createUserWithEmailAndPassword(auth, email, password)
        try { await updateProfile(cred.user, { displayName: uname }) } catch {}
        await ProfilesService.ensureProfile(cred.user.uid, email, uname, 'client', companyName || undefined)
        onLogin({ id: cred.user.uid, username: uname, email }, rememberMe)
        clearForm()
        setNewUsername('')
        onClose()
      }
    } catch (err: any) {
      console.error('🚨 Login error details:', err)
      console.error('🚨 Error code:', err.code)
      console.error('🚨 Error message:', err.message)
      
      // W przypadku błędu, włącz z powrotem listenery (tylko dla login)
      if (mode === 'login' && onSetRoleSwitching) {
        console.log('❌ Re-enabling listeners after client login error')
        onSetRoleSwitching(false)
      }
      const userFriendlyMessage = translateFirebaseError(err)
      setError(userFriendlyMessage)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal">
      <div className="modal-content" style={{ maxWidth: '400px', padding: '30px' }}>
        <div className="modal-header">
          <h3 style={{ textAlign: 'center', width: '100%' }}>{mode === 'login' ? 'Logowanie do Panelu' : 'Rejestracja w Systemie'}</h3>
        </div>
        
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <p style={{ color: '#666', fontSize: '14px', margin: '0' }}>
            Dostęp do Panelu tylko dla Autoryzowanych Dystrybutorów
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              E-mail lub nazwa użytkownika:
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              placeholder="wpisz e-mail lub nazwę użytkownika"
              required
            />
          </div>

          {mode === 'register' && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Nazwa użytkownika (opcjonalnie):
              </label>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                placeholder="np. jan_kowalski"
              />
            </div>
          )}

          {mode === 'register' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Nazwa firmy (opcjonalnie):
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                placeholder="np. ABC Sp. z o.o."
              />
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Hasło:
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 40px 8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                placeholder="wpisz hasło"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  color: '#666',
                  padding: '4px'
                }}
                title={showPassword ? 'Ukryj hasło' : 'Pokaż hasło'}
              >
                {showPassword ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {mode === 'register' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Potwierdzenie hasła:
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 40px 8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="powtórz hasło"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#666',
                    padding: '4px'
                  }}
                  title={showPassword ? 'Ukryj hasło' : 'Pokaż hasło'}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div style={{
              color: '#dc3545',
              fontSize: '14px',
              marginBottom: '15px',
              padding: '8px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px'
            }}>
              {error}
            </div>
          )}

          {/* First row: Remember me checkbox only (register link hidden) */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '15px' }}>
            {/* Register/Login link - TYMCZASOWO UKRYTE */}
            {false && (
              <button
                type="button"
                onClick={() => {
                  setError('')
                  setPassword('')
                  setConfirmPassword('')
                  setNewUsername('')
                  setMode(mode === 'login' ? 'register' : 'login')
                }}
                style={{
                  background: 'transparent',
                  color: 'var(--accent)',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px'
                }}
              >
                {mode === 'login' ? 'Nie masz konta? Zarejestruj się' : 'Masz konto? Zaloguj się'}
              </button>
            )}

            {/* Remember me checkbox - only for login */}
            {mode === 'login' && (
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#555' }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ 
                    marginRight: '8px',
                    accentColor: 'var(--accent)'
                  }}
                />
                Zapamiętaj mnie na tym urządzeniu
              </label>
            )}
          </div>

          {/* Second row: Action buttons */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '0',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer'
              }}
            >
              Anuluj
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '0',
                backgroundColor: 'var(--accent)',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              {mode === 'login' ? (loading ? 'Logowanie…' : 'Zaloguj się') : (loading ? 'Rejestracja…' : 'Zarejestruj się')}
            </button>
          </div>
        </form>

        {/* Google Sign-In - TYMCZASOWO UKRYTE */}
        {false && (
          <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 16 }}>
            <button
              type="button"
              onClick={async () => {
                if (loadingGoogle) return
                setError('')
                setLoadingGoogle(true)
                
                // RADYKALNE ROZWIĄZANIE: Wyłącz wszystkie listenery przed Google login
                if (onSetRoleSwitching) {
                  console.log('🛑 Disabling all listeners for Google client login')
                  onSetRoleSwitching(true)
                }
                
                // Sprawdź czy admin jest zalogowany i wyloguj go przed Google logowaniem usera
                if (isAdminLoggedIn && onAdminLogout) {
                  console.log('🔄 Admin → User switch (Google): logging out admin first')
                  alert('Zalogowano się jako użytkownik. Zostaniesz wylogowany z konta Administratora.')
                  onAdminLogout()
                }
                
                try {
                  const provider = new GoogleAuthProvider()
                  // Optional: enforce account selection
                  provider.setCustomParameters({ prompt: 'select_account' })
                  const cred = await signInWithPopup(auth, provider)
                  const u = cred.user
                  const email = u.email || ''
                  // Użyj displayName lub wygeneruj z emaila
                  const username = u.displayName || email.split('@')[0] || 'user'
                  
                  // Upewnij się, że profil istnieje z rolą 'client'
                  const profile = await ProfilesService.ensureProfile(u.uid, email, username, 'client')
                  
                  // Check if user profile is marked as deleted
                  if (profile.deleted) {
                    alert('To konto zostało usunięte przez administratora.')
                    await auth.signOut()
                    throw new Error('Konto usunięte')
                  }
                  
                  // DODATKOWE ZABEZPIECZENIE: Sprawdź rolę użytkownika przed zalogowaniem
                  console.log('🔐 Final Google role verification before login...')
                  await new Promise(resolve => setTimeout(resolve, 500)) // Opóźnienie dla stabilności
                  
                  const finalProfile = await ProfilesService.getByUid(u.uid)
                  if (!finalProfile || finalProfile.deleted) {
                    await auth.signOut()
                    throw new Error('Profil użytkownika nie został znaleziony lub konto zostało usunięte')
                  }
                  
                  console.log('✅ Final Google verification passed, role:', finalProfile.role)
                  
                  // Obsługa logowania w zależności od roli
                  if (finalProfile.role === 'admin') {
                    // Użytkownik ma rolę admin
                    if (onAdminLogin) {
                      onAdminLogin(rememberMe)
                    } else {
                      throw new Error('Brak obsługi logowania administratora')
                    }
                  } else if (finalProfile.role === 'client') {
                    // Użytkownik ma rolę client
                    onLogin({ id: u.uid, username, email }, rememberMe)
                  } else {
                    await auth.signOut()
                    throw new Error('Nieznana rola użytkownika')
                  }
                  
                  clearForm()
                  onClose()
                  
                  // Włącz z powrotem listenery po krótkiej chwili
                  setTimeout(() => {
                    if (onSetRoleSwitching) {
                      console.log('✅ Re-enabling listeners after Google client login')
                      onSetRoleSwitching(false)
                    }
                  }, 2000) // 2 sekundy żeby się wszystko ustabilizowało
                  
                } catch (err: any) {
                  // W przypadku błędu, włącz z powrotem listenery
                  if (onSetRoleSwitching) {
                    console.log('❌ Re-enabling listeners after Google client login error')
                    onSetRoleSwitching(false)
                  }
                  setError(translateFirebaseError(err))
                } finally {
                  setLoadingGoogle(false)
                }
              }}
              style={{
                width: '100%',
                padding: '10px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                fontWeight: 500
              }}
            >
              <span style={{ display: 'inline-block', width: 18, height: 18, background: 'url(https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg) no-repeat center/contain' }} />
              {loadingGoogle ? 'Łączenie z Google…' : 'Zaloguj przez Google'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ClientLogin
